import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';
import { useApp } from '@/contexts/AppContext';
import { useAssignServicePlan, type ServicePlanWithCaps } from '@/hooks/useServicePlans';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  plans: ServicePlanWithCaps[];
}

export function AssignPlanDialog({ open, onOpenChange, plans }: Props) {
  const { properties } = useApp();
  const assign = useAssignServicePlan();

  const [propertyId, setPropertyId] = useState('');
  const [planId, setPlanId] = useState('');
  const [overrideDollars, setOverrideDollars] = useState('');

  const activePlans = plans.filter((p) => p.is_active);

  const handleAssign = () => {
    if (!propertyId || !planId) {
      toast.error('Property and plan are required');
      return;
    }

    const overrideCents = overrideDollars.trim()
      ? Math.round(parseFloat(overrideDollars) * 100)
      : undefined;

    if (overrideCents !== undefined && (isNaN(overrideCents) || overrideCents < 0)) {
      toast.error('Price override must be a valid number');
      return;
    }

    assign.mutate(
      {
        property_id: propertyId,
        service_plan_id: planId,
        price_override_cents: overrideCents,
      },
      {
        onSuccess: () => {
          toast.success('Plan assigned (pending)');
          setPropertyId('');
          setPlanId('');
          setOverrideDollars('');
          onOpenChange(false);
        },
        onError: (err) => {
          toast.error(`Failed to assign: ${err instanceof Error ? err.message : 'Unknown error'}`);
        },
      },
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Assign Plan to Property</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="space-y-2">
            <Label>Property</Label>
            <Select value={propertyId} onValueChange={setPropertyId}>
              <SelectTrigger>
                <SelectValue placeholder="Select property" />
              </SelectTrigger>
              <SelectContent>
                {properties.map((p) => (
                  <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>Service Plan</Label>
            <Select value={planId} onValueChange={setPlanId}>
              <SelectTrigger>
                <SelectValue placeholder="Select plan" />
              </SelectTrigger>
              <SelectContent>
                {activePlans.map((p) => (
                  <SelectItem key={p.id} value={p.id}>
                    {p.name} — ${(p.price_cents / 100).toFixed(2)}/{p.billing_interval === 'annual' ? 'yr' : 'mo'}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>Price Override ($)</Label>
            <Input
              type="number"
              min="0"
              step="0.01"
              value={overrideDollars}
              onChange={(e) => setOverrideDollars(e.target.value)}
              placeholder="Leave blank to use plan price"
            />
            <p className="text-xs text-muted-foreground">
              Optional. Overrides the plan price for this property only.
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={handleAssign} disabled={assign.isPending}>
            {assign.isPending && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            Assign Plan
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
